/** Moldura da tela de login (usada no login de verdade e na prévia de Aparência). */
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import type { LoginLook } from "@/lib/brand";

export interface LoginFrameProps {
  bg?: string | null; look?: LoginLook | null; brandColor?: string | null;
  bgLogoUrl?: string | null; cardLogoUrl?: string | null; bgImageUrl?: string | null; officeName: string; note?: string | null;
  children: ReactNode; preview?: boolean;
}

export const LOGIN_DEFAULTS = { bg: "#EEF1F5", card: "#FFFFFF", text: "#1F2937", buttonText: "#FFFFFF", bgLogoSize: 120, cardLogoSize: 64, tileSize: 140, tileGap: 60, tileOpacity: 35 };

/** Mosaico: desenha um "ladrilho" com a logo (com espaço e, se intercalado, a segunda linha deslocada) para repetir no fundo. */
function useTile(url: string | null | undefined, on: boolean, size: number, gap: number, opacity: number, pattern: string, scale: number): string | null {
  const [tile, setTile] = useState<string | null>(null);
  useEffect(() => {
    if (!on || !url) { setTile(null); return; }
    let alive = true;
    const img = new Image();
    img.onload = () => {
      if (!alive) return;
      const w = Math.max(10, Math.round(size * scale)), h = Math.max(6, Math.round((w * (img.naturalHeight || 1)) / (img.naturalWidth || 1)));
      const g = Math.round(gap * scale);
      const cw = w + g, ch = h + g;
      const c = document.createElement("canvas");
      const stagger = pattern === "intercalado";
      c.width = cw; c.height = stagger ? ch * 2 : ch;
      const ctx = c.getContext("2d")!;
      ctx.globalAlpha = opacity / 100;
      ctx.drawImage(img, g / 2, g / 2, w, h);
      if (stagger) { ctx.drawImage(img, g / 2 + cw / 2, ch + g / 2, w, h); ctx.drawImage(img, g / 2 - cw / 2, ch + g / 2, w, h); }
      try { setTile(c.toDataURL("image/png")); } catch { setTile(null); }
    };
    img.src = url;
    return () => { alive = false; };
  }, [url, on, size, gap, opacity, pattern, scale]);
  return tile;
}

export function LoginFrame({ bg, look, brandColor, bgLogoUrl, cardLogoUrl, bgImageUrl, officeName, note, children, preview }: LoginFrameProps): JSX.Element {
  const L = look ?? {};
  const c1 = bg || LOGIN_DEFAULTS.bg;
  const color = L.bg2 ? `linear-gradient(135deg, ${c1} 0%, ${L.bg2} 100%)` : c1;
  const mode = L.bgMode ?? "cor";
  const o = (L.bgOverlay ?? 0) / 100;
  const veil = o ? `linear-gradient(rgba(0,0,0,${o}), rgba(0,0,0,${o})), ` : "";
  const tile = useTile(bgImageUrl, mode === "mosaico", L.tileSize ?? LOGIN_DEFAULTS.tileSize, L.tileGap ?? LOGIN_DEFAULTS.tileGap, L.tileOpacity ?? LOGIN_DEFAULTS.tileOpacity, L.tilePattern ?? "intercalado", preview ? 0.35 : 1);
  const fit = L.bgFit === "inteira" ? "contain" : "cover";
  let style: CSSProperties = { background: color };
  if (mode === "imagem" && bgImageUrl) style = { background: `${veil}url("${bgImageUrl}") center / ${fit} no-repeat, ${color}` };
  if (mode === "mosaico" && tile) style = { background: `${veil}url("${tile}") repeat, ${color}` };
  const cardStyle = {
    background: L.card || LOGIN_DEFAULTS.card, color: L.text || LOGIN_DEFAULTS.text,
    ["--lgn-text" as string]: L.text || LOGIN_DEFAULTS.text,
    ["--lgn-btn" as string]: L.button || brandColor || "var(--primary)",
    ["--lgn-btn-text" as string]: L.buttonText || LOGIN_DEFAULTS.buttonText,
  } as CSSProperties;
  const pos = L.bgLogo ?? "nenhuma";
  const card = (
    <div className="lgn-card" style={cardStyle}>
      {(L.cardLogo ?? true) && cardLogoUrl && <img className="lgn-logo" src={cardLogoUrl} alt={officeName} style={{ maxHeight: L.cardLogoSize ?? LOGIN_DEFAULTS.cardLogoSize }} />}
      {(L.showName ?? true) && <div className="lgn-name"><div className="lgn-title">{officeName}</div><div className="lgn-sub">Sistema jurídico</div></div>}
      {children}
      {note && <div className="lgn-note">{note}</div>}
    </div>
  );
  if (mode === "dividida") {
    // Metade com a imagem/logo grande, metade com o login: nada fica escondido atrás do cartão.
    const art: CSSProperties = { background: bgImageUrl ? `${veil}url("${bgImageUrl}") center / ${fit} no-repeat, ${color}` : color };
    return (
      <div className={`lgn split ${L.splitSide === "direita" ? "art-right" : ""} ${preview ? "preview" : ""}`}>
        <div className="lgn-art" style={art} />
        <div className="lgn-side" style={{ background: L.card || LOGIN_DEFAULTS.card }}>{card}</div>
      </div>
    );
  }
  return (
    <div className={`lgn ${preview ? "preview" : ""}`} style={style}>
      <div className={`lgn-inner pos-${pos}`}>
        {pos !== "nenhuma" && bgLogoUrl && <img className="lgn-bglogo" src={bgLogoUrl} alt={officeName} style={{ height: L.bgLogoSize ?? LOGIN_DEFAULTS.bgLogoSize }} />}
        {card}
      </div>
    </div>
  );
}
